import { useQuery } from '@tanstack/react-query'
import { QueryCacheEnum } from 'constants/ReactQuery.constants'
import { Auth } from 'aws-amplify'
import { UseQueryOptions } from 'types/ReactQuery.type'
import { UserWhoami } from 'features/auth/AzimuthAuth.type'
import { fetchFirebaseToken, fetchWhoAmIUsingGET } from 'apis/AzimuthAuth.api'
import { extractWhoamiResources } from 'converters/Auth.converter'
import { useMemo } from 'react'
import { Identity, Maybe, Organization } from '@vendorflow/types'
import { CognitoUser } from 'amazon-cognito-identity-js'
import { generateSignature } from 'services/Utils.service'
import { FIVE_MINUTES } from 'pages/WorkspacePage/Workspace.constants'
import { useParams } from 'react-router-dom'
import { WorkspaceChannelParams } from 'types/Router.types'
import { useSelector } from 'react-redux'
import { selectActiveEnvy } from 'features/auth/Auth.slice'
import ms from 'ms'

export function currentAuthenticatedUserQueryKey() {
  return [QueryCacheEnum.AUTH, QueryCacheEnum.COGNITO, QueryCacheEnum.USER]
}

export function getUseIsOrgAdminKey(envy: Maybe<string>) {
  return [QueryCacheEnum.ORGANIZATIONS, envy, QueryCacheEnum.IS_ORG_ADMIN]
}

export function useCurrentAuthenticatedUser(options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true

  return useQuery<CognitoUser, Error>(currentAuthenticatedUserQueryKey(), () => Auth.currentAuthenticatedUser(), {
    enabled,
    staleTime: FIVE_MINUTES,
    cacheTime: FIVE_MINUTES,
    refetchInterval: FIVE_MINUTES,
    retry: false,
  })
}

export function useIsVfAdmin(options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true

  return useQuery<boolean, Error>(
    [QueryCacheEnum.AUTH, QueryCacheEnum.COGNITO, QueryCacheEnum.IS_ADMIN],
    async () => {
      const COGNITO_GROUPS = 'cognito:groups'
      const ADMIN_ROLE = 'VF_ADMIN'
      const session = await Auth.currentSession()
      const payload = session.getAccessToken().decodePayload()

      return Array.isArray(payload[COGNITO_GROUPS]) && payload[COGNITO_GROUPS].includes(ADMIN_ROLE)
    },
    { enabled }
  )
}

export function useIsOrgAdmin(envy: Maybe<string>) {
  const whoami = useWhoAmI()

  return useMemo(() => {
    if (!whoami.data) return { data: false, dataUpdatedAt: 0 }

    return {
      data: whoami.data.administers.some((it) => it.id === envy),
      dataUpdatedAt: whoami.dataUpdatedAt,
      isInitialLoading: whoami.isInitialLoading,
    }
  }, [whoami.dataUpdatedAt])
}

export function useIsAdmin() {
  const envy = useMyOrgId()
  const isVfAdmin = useIsVfAdmin()
  const isOrgAdmin = useIsOrgAdmin(envy)

  return useMemo(
    () => ({
      data: Boolean(isVfAdmin.data || isOrgAdmin.data),
      dataUpdatedAt: Math.max(isVfAdmin.dataUpdatedAt, isOrgAdmin.dataUpdatedAt),
      isInitialLoading: isVfAdmin.isInitialLoading || isOrgAdmin.isInitialLoading,
    }),
    [envy, isVfAdmin.dataUpdatedAt, isOrgAdmin.dataUpdatedAt]
  )
}

export function whoAmIQueryKey() {
  return [QueryCacheEnum.AUTH, QueryCacheEnum.WHO_AM_I]
}

export async function whoAmIQueryFn() {
  const result = await fetchWhoAmIUsingGET()
  return extractWhoamiResources(result)
}

export function useWhoAmI() {
  return useQuery<UserWhoami, Error, UserWhoami, ReturnType<typeof whoAmIQueryKey>>({
    queryKey: whoAmIQueryKey(),
    queryFn: whoAmIQueryFn,
    staleTime: ms('10m'),
    cacheTime: ms('10m'),
    retry: false,
  })
}

export function firebaseTokenQueryKey(bob: Maybe<string>) {
  return [QueryCacheEnum.FIREBASE, QueryCacheEnum.TOKEN, bob]
}

export async function firebaseTokenQueryFn({ queryKey }: { queryKey: ReturnType<typeof firebaseTokenQueryKey> }) {
  const bob = queryKey[2]!
  return fetchFirebaseToken(bob)
}

export function useFirebaseToken() {
  const bobId = useMyIdentityId()

  return useQuery<string, Error, string, ReturnType<typeof firebaseTokenQueryKey>>(
    firebaseTokenQueryKey(bobId),
    firebaseTokenQueryFn,
    {
      enabled: !!bobId,
      staleTime: 0,
      cacheTime: 0,
    }
  )
}

export function useMyIdentity(): Identity | null {
  const { data = null, dataUpdatedAt } = useWhoAmI()
  return useMemo(() => data?.identity || null, [dataUpdatedAt])
}

export function useMyIdentityId(): string | null {
  const identity = useMyIdentity()
  return identity?.id || null
}

export function useMyIdentityEmail(): string | null {
  const identity = useMyIdentity()
  return identity?.emails[0] || null
}

export function useMyIdentityDisplayName(): string | null {
  const identity = useMyIdentity()
  return identity?.displayName || null
}

export function useMyIdentityPhone(): string | null {
  const identity = useMyIdentity()
  return identity?.phones[0]?.number || null
}

export function useMyIdentitySignature(): string | null {
  const displayName = useMyIdentityDisplayName()
  const phone = useMyIdentityPhone()
  return generateSignature(phone, displayName)
}

export function useMyOrgs(): Organization[] {
  const { data = null, dataUpdatedAt } = useWhoAmI()
  return useMemo(() => data?.organizations || [], [dataUpdatedAt])
}

/**
 * This will judge the envy using the following waterfall:
 * 1. Value of envy param in the URL (if one exists)
 * 2. Value of the selected org in redux (if one exists)
 * 3. The first organization in the user's list of organizations'
 */
export function useMyOrg(): Organization | null {
  const params = useParams<WorkspaceChannelParams>()
  const activeEnvy = useSelector(selectActiveEnvy)
  const whoami = useWhoAmI()
  return useMemo(() => {
    if (!whoami.dataUpdatedAt || !whoami.data?.organizations?.length) {
      return null
    }
    if (activeEnvy) {
      return whoami.data.organizations.find((o) => o.id === activeEnvy) || null
    }
    if (params.envy) {
      return whoami.data.organizations.find((o) => o.id === params.envy) || null
    }

    return whoami.data.organizations[0]
  }, [activeEnvy, params.envy, whoami.dataUpdatedAt])
}

export function useMyOrgId(): string | null {
  const org = useMyOrg()
  return org?.id || null
}

export function useMyOrgName(): string | null {
  const org = useMyOrg()
  return org?.name || null
}
